import { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTimes,
  faStickyNote,
  faCheckCircle,
  faInfoCircle,
  faSpinner,
  faEdit,
  faTag,
} from '@fortawesome/free-solid-svg-icons';
import toastService from '@frontend/AppSettings/services/ToastService';
import { IClientNote } from '@frontend/Models/Notes/NotesModels';
import { updateStakeholderNote } from '../utils/orbitStakeholderNotes';
import '../AddNotePage/AddNotePage.css';

interface EditNotePageProps {
  onClose: () => void;
  stakeholderId: string;
  type: 'individual' | 'organization';
  note: IClientNote;
  onNoteUpdated?: () => void;
  existingTags?: string[];
}

const EditNotePage: React.FC<EditNotePageProps> = ({
  onClose,
  stakeholderId,
  type,
  note,
  onNoteUpdated,
  existingTags = [],
}) => {
  const [noteText, setNoteText] = useState<string>(note.note_text || '');
  const [tagInput, setTagInput] = useState<string>('');
  const [tags, setTags] = useState<string[]>(Array.isArray(note.tags) ? [...note.tags] : []);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string>('');

  const handleTextChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNoteText(e.target.value);
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && tagInput.trim()) {
      e.preventDefault();
      const trimmedTag = tagInput.trim();
      if (!tags.includes(trimmedTag)) {
        setTags([...tags, trimmedTag]);
      }
      setTagInput('');
    }
  };

  const removeTag = (tagToRemove: string) => {
    setTags(tags.filter((tag) => tag !== tagToRemove));
  };

  const tagSuggestions = existingTags
    .filter(
      (tag) =>
        !tags.includes(tag) &&
        tag.toLowerCase().includes(tagInput.toLowerCase().trim())
    )
    .slice(0, 5);

  const availableTags = existingTags.filter((tag) => !tags.includes(tag));

  const addTagFromSuggestion = (tag: string) => {
    if (!tags.includes(tag)) {
      setTags([...tags, tag]);
    }
    setTagInput('');
  };

  const handleClose = () => {
    setError('');
    onClose();
  };

  const handleNoteUpdate = async () => {
    setLoading(true);
    setError('');

    if (!noteText.trim()) {
      setLoading(false);
      setError('Please enter a note.');
      return;
    }

    try {
      await updateStakeholderNote(stakeholderId, note.noteid, {
        note_text: noteText.trim(),
        tags,
      });

      toastService.success('Note updated successfully');
      onNoteUpdated?.();
      handleClose();
    } catch (err: any) {
      console.error('Error updating note:', err);
      setError(err.message || 'Failed to update the note. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="add-note-overlay" onClick={handleClose}>
      <div className="add-note-panel" onClick={(e) => e.stopPropagation()}>
        <div className="add-note-header">
          <div className="add-note-header-left">
            <div className="add-note-title-row">
              <div className="add-note-header-icon">
                <FontAwesomeIcon icon={faEdit} />
              </div>
              <div className="add-note-header-text">
                <h2 className="add-note-title">Edit Note</h2>
                <p className="add-note-subtitle">
                  Edit note for this{' '}
                  {type === 'organization' ? 'organization' : 'stakeholder'}
                </p>
              </div>
            </div>
          </div>
          <button
            type="button"
            className="add-note-close"
            onClick={handleClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faTimes} />
          </button>
        </div>

        <div className="add-note-content">
          <div className="add-note-section add-note-section-blue">
            <div className="add-note-section-header">
              <div className="add-note-section-icon-wrapper add-note-icon-blue">
                <FontAwesomeIcon icon={faStickyNote} />
              </div>
              <h3 className="add-note-section-title">Note</h3>
            </div>
            <div className="add-note-section-content">
              <div className="add-note-textarea-wrapper">
                <textarea
                  value={noteText}
                  onChange={handleTextChange}
                  placeholder="Type your note here..."
                  rows={8}
                  className="add-note-textarea"
                  maxLength={2000}
                />
                <div className="add-note-char-counter">{noteText.length}/2000</div>
              </div>
            </div>
          </div>

          <div className="add-note-section add-note-section-purple">
            <div className="add-note-section-header">
              <div className="add-note-section-icon-wrapper add-note-icon-purple">
                <FontAwesomeIcon icon={faTag} />
              </div>
              <h3 className="add-note-section-title">Tags</h3>
            </div>
            <div className="add-note-section-content">
              <div className="add-note-tag-input-wrapper">
                <label className="add-note-tag-label">Add Tag</label>
                <div className="add-note-tag-input-container">
                  <input
                    type="text"
                    name="tagInput"
                    value={tagInput}
                    onChange={(e) => setTagInput(e.target.value)}
                    onKeyDown={handleTagInputKeyDown}
                    placeholder="Type a tag and press Enter"
                    className="add-note-tag-input"
                    autoComplete="off"
                  />
                  {tagInput.trim() && tagSuggestions.length > 0 && (
                    <div className="add-note-tag-suggestions">
                      {tagSuggestions.map((tag) => (
                        <button
                          key={tag}
                          type="button"
                          className="add-note-tag-suggestion-item"
                          onClick={() => addTagFromSuggestion(tag)}
                        >
                          <FontAwesomeIcon
                            icon={faTag}
                            className="add-note-suggestion-icon"
                          />
                          {tag}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {tags.length > 0 && (
                <div className="add-note-tags-preview">
                  <span className="add-note-tags-label">Selected:</span>
                  {tags.map((tag) => (
                    <span key={tag} className="add-note-tag-preview add-note-tag-selected">
                      {tag}
                      <button
                        type="button"
                        className="add-note-remove-tag"
                        onClick={() => removeTag(tag)}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              )}

              {availableTags.length > 0 && (
                <div className="add-note-available-tags">
                  <span className="add-note-tags-label">Available tags:</span>
                  <div className="add-note-tags-list">
                    {availableTags.map((tag) => (
                      <button
                        key={tag}
                        type="button"
                        className="add-note-tag-available"
                        onClick={() => addTagFromSuggestion(tag)}
                        title={`Add "${tag}" tag`}
                      >
                        + {tag}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              <div className="add-note-info-box">
                <FontAwesomeIcon icon={faInfoCircle} className="add-note-info-icon" />
                <span>
                  {existingTags.length > 0
                    ? 'Click an available tag to add it, or type a new tag and press Enter.'
                    : 'Add tags to categorize your note. Press Enter to add a tag.'}
                </span>
              </div>
            </div>
          </div>

          {error && <div className="add-note-error">{error}</div>}
        </div>

        <div className="add-note-footer">
          <div className="add-note-footer-info">
            <FontAwesomeIcon icon={faInfoCircle} className="add-note-footer-info-icon" />
            <span>Changes will be saved and visible to your team</span>
          </div>
          <div className="add-note-footer-buttons">
            <button
              type="button"
              className="add-note-button add-note-button-cancel"
              onClick={handleClose}
              disabled={loading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="add-note-button add-note-button-primary"
              onClick={handleNoteUpdate}
              disabled={loading || !noteText.trim()}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={faCheckCircle} />
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditNotePage;
